/** Shared character editor for onboarding and the in-game Character screen. */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import {
  BODY_IDS, HEAD_CATALOG, PLAYER_HEAD_CATALOG, validateFaceId, HAIR_CATALOG, validateHairId,
  BODY_BUILDS, validateBodyBuild, BODY_ARCHETYPES, BODY_SHAPE_AXES, bodyShapeKey, validateBodyShape, type BodyShapeAxis,
  CHARACTER_NAME_MAX, CHARACTER_NAME_MIN, DEFAULT_STYLE, normalizeCharacterName,
  EYE_COLORS, AURA_COLORS, HAIR_COLORS, SKIN_COLORS, OUTFIT_COLORS, STYLE_PARTS, STYLE_VARIANTS,
  STARTER_UNLOCKS, clampStyleToUnlocks, type CharacterCreationDraft, type CharacterStyle, type StylePart,
} from "@hyperlight/shared";
import { Avatar } from "../character/avatar";
import { wearOutfit } from "./characterAppearance";
import "./characterCreator.css";

type ColorField = "skin" | "hairColor" | "eyeColor" | "auraColor" | "shirtColor" | "pantsColor";
type EditorMode = "appearance" | "outfit";
type AppearancePage = "face" | "body" | "hair";
export interface CharacterOutfit { id: string; name: string; style: CharacterStyle }
export interface CharacterCreatorOptions {
  mode: "create" | "edit";
  name: string;
  style: CharacterStyle;
  initialMode?: EditorMode;
  /** Optional presentation overrides; submission and persistence stay caller-owned. */
  title?: string;
  submitLabel?: string;
  saveNote?: string;
  /** Initial appearance-preview setting. The outfit toggle remains available. */
  showClothing?: boolean;
  onSubmit(draft: CharacterCreationDraft, outfit?: CharacterOutfit | null): Promise<void>;
  onCancel?(): void;
  ownedTokens?(): readonly string[];
  wardrobe?: {
    list(): Promise<CharacterOutfit[]>;
    save(id: string | null, name: string, style: CharacterStyle): Promise<CharacterOutfit>;
    remove(id: string): Promise<void>;
  };
  stats?: { render(): string; onAction(track: string): void };
}
const hex = (v: number): string => `#${v.toString(16).padStart(6, "0")}`;
const PART_LABELS: Record<StylePart, string> = {
  head: "Head", torso: "Torso", arms: "Arms", legs: "Legs", feet: "Feet",
};
const COLORS: [string, readonly number[], ColorField, readonly string[]][] = [
  ["#cc-shirtc", OUTFIT_COLORS, "shirtColor", ["Slate", "Charcoal", "Sand", "Moss", "Rust", "Plum", "Ivory", "Navy"]],
  ["#cc-pantsc", OUTFIT_COLORS, "pantsColor", ["Slate", "Charcoal", "Sand", "Moss", "Rust", "Plum", "Ivory", "Navy"]],
  ["#cc-skin", [0xffffff, ...SKIN_COLORS], "skin", ["Original", "Light", "Warm", "Golden", "Tan", "Deep", "Rich"]],
  ["#cc-hairc", HAIR_COLORS, "hairColor", ["Black", "Soft black", "Dark brown", "Brown", "Chestnut", "Light brown", "Blonde", "Grey", "White", "Auburn"]],
  ["#cc-eyec", EYE_COLORS, "eyeColor", ["Brown", "Hazel", "Blue", "Green", "Grey", "Amber", "Violet"]],
  ["#cc-aurac", AURA_COLORS, "auraColor", ["White", "Ice", "Cyan", "Gold", "Ember", "Crimson", "Violet", "Jade"]],
];

export class CharacterCreator {
  active = false;
  get saving(): boolean { return this.busy; }
  private readonly root = document.createElement("section");
  private readonly mannequin: Avatar;
  private readonly lights = new THREE.Group();
  private orbit: OrbitControls | null = null;
  private options: CharacterCreatorOptions | null = null;
  private style: CharacterStyle = { ...DEFAULT_STYLE };
  private editorMode: EditorMode = "appearance";
  private page: AppearancePage = "face";
  private focus: "face" | "body" = "face";
  private showClothing = false;
  private busy = false;
  private wardrobeBusy = false;
  private outfits: CharacterOutfit[] = [];
  private selectedOutfit: CharacterOutfit | null | undefined;
  private session = 0;
  private fadeTimer: number | null = null;
  private previousFov = 50;
  private previousFilmOffset = 0;
  private previousFocus: HTMLElement | null = null;
  private previewRevision = 0;
  private frameSize = "";
  private disabledForSave = new Map<HTMLInputElement | HTMLSelectElement | HTMLButtonElement, boolean>();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly canvas: HTMLCanvasElement,
    private readonly scene: THREE.Scene,
  ) {
    this.root.id = "character-creator";
    this.root.setAttribute("aria-hidden", "true");
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-labelledby", "cc-title");
    this.root.innerHTML = `
      <div class="cc-preview" aria-label="Character preview">
        <div class="cc-preview-heading"><span class="cc-kicker">SOUL MERIDIAN</span><h1 id="cc-preview-name">Your character</h1><p id="cc-preview-caption">A face for your journey.</p></div>
        <div class="cc-preview-tools">
          <div class="cc-view-controls" aria-label="Preview camera">
            <button type="button" data-focus="body">Full body</button><button type="button" data-focus="face">Face</button>
            <span></span><button type="button" data-rotate="-1" aria-label="Rotate left">↶</button><button type="button" data-rotate="1" aria-label="Rotate right">↷</button><button type="button" id="cc-reset-view" aria-label="Reset camera">↺</button>
          </div>
          <div class="cc-preview-settings"><label>Expression <select id="cc-expression"><option value="neutral">Neutral</option><option value="happy">Happy</option><option value="sad">Sad</option><option value="angry">Angry</option><option value="confused">Confused</option><option value="surprised">Surprised</option><option value="disgusted">Disgusted</option><option value="fearful">Fearful</option><option value="determined">Determined</option><option value="speaking">Speaking</option></select></label><button type="button" id="cc-clothing" aria-pressed="false">Show outfit</button></div>
          <p>Drag to rotate · scroll to zoom</p>
        </div>
        <span id="cc-loading" role="status" hidden>Updating preview…</span>
      </div>
      <form class="cc-panel" novalidate>
        <header><div><span class="cc-kicker">YOUR JOURNEY</span><h2 id="cc-title">Create your character</h2></div><button type="button" id="cc-stats-toggle" aria-expanded="false" hidden>Stats</button></header>
        <div id="cc-identity"><label class="cc-field">Character name<input id="cc-name" autocomplete="off" minlength="${CHARACTER_NAME_MIN}" maxlength="${CHARACTER_NAME_MAX}" placeholder="Choose your name" aria-describedby="cc-name-note cc-error" /></label><p id="cc-name-note" class="cc-note">The name other travelers will know you by.</p></div>
        <nav class="cc-modes" aria-label="Character mode"><button type="button" data-mode="appearance">Appearance</button><button type="button" data-mode="outfit">Outfit</button></nav>
        <div class="cc-scroll">
          <div data-mode-page="appearance">
            <nav class="cc-tabs" aria-label="Appearance sections"><button type="button" data-tab="face">Face</button><button type="button" data-tab="body">Body</button><button type="button" data-tab="hair">Hair & color</button></nav>
            <section data-page="face"><div class="cc-section-heading"><h3>Choose your face</h3><span id="cc-face-count"></span></div><div id="cc-faces" class="cc-face-cards"></div><p class="cc-note" id="cc-face-note">Every face has its own expressions.</p><fieldset><legend>Skin tone</legend><div id="cc-skin" class="cc-swatches"></div></fieldset><fieldset><legend>Eye color</legend><div id="cc-eyec" class="cc-swatches"></div></fieldset></section>
            <section data-page="body" hidden><h3>Your silhouette</h3><p class="cc-description">Choose a build. Make it your own.</p><label class="cc-field">Body type<select id="cc-body"></select></label><fieldset class="cc-builds"><legend>Body build</legend><div id="cc-builds" class="cc-build-cards">${BODY_BUILDS.map(b => `<button type="button" class="cc-build-card" data-body-build="${b.id}" aria-pressed="false"><span>${b.label}<span class="cc-choice-mark" aria-hidden="true">✓</span></span><small>${b.description}</small></button>`).join("")}</div></fieldset><details class="cc-proportions"><summary>Adjust proportions</summary><p class="cc-note">Fine-tune this build. Choosing another build resets these adjustments.</p><label class="cc-field">Proportion preset<select id="cc-archetype"></select></label><div id="cc-shape-controls"></div></details><fieldset><legend>Aura color</legend><div id="cc-aurac" class="cc-swatches"></div></fieldset></section>
            <section data-page="hair" hidden><h3>The finishing touches</h3><p class="cc-description">A familiar look, or a fresh start.</p><label class="cc-field">Hairstyle<select id="cc-hair"></select></label><fieldset><legend>Hair color</legend><div id="cc-hairc" class="cc-swatches"></div></fieldset><div id="cc-hairstyles" class="cc-hair-cards" aria-label="Hairstyles"></div></section>
          </div>
          <section data-mode-page="outfit" hidden><h3>Dress for the journey</h3><p class="cc-description" id="cc-outfit-note"></p><div class="cc-parts">${STYLE_PARTS.map(p => `<label class="cc-field">${PART_LABELS[p]}<select data-part="${p}" aria-label="${PART_LABELS[p]}"></select></label>`).join("")}</div><fieldset><legend>Base shirt color</legend><div id="cc-shirtc" class="cc-swatches"></div></fieldset><fieldset><legend>Base trouser color</legend><div id="cc-pantsc" class="cc-swatches"></div></fieldset><div id="cc-wardrobe" hidden><div class="cc-section-heading"><h3>Saved outfits</h3></div><p class="cc-note">Try on a look. Your appearance stays yours.</p><div id="cc-outfits"></div><label class="cc-field">Outfit name<input id="cc-outfit-name" maxlength="24" placeholder="Name this outfit" autocomplete="off" /></label><div class="cc-outfit-actions"><button type="button" id="cc-outfit-save">Save as new</button><button type="button" id="cc-outfit-update" disabled>Update selected</button></div><p id="cc-wardrobe-status" role="status"></p></div></section>
        </div>
        <div class="cc-draft-tools"><button id="cc-random" type="button">Randomize appearance</button><button id="cc-revert" type="button">Reset changes</button></div>
        <div id="cc-error" role="alert"></div><footer><button id="cc-cancel" type="button" class="cc-secondary">Cancel</button><button id="cc-submit" type="submit">Begin journey <span aria-hidden="true">→</span></button></footer>
        <p id="cc-save-note" class="cc-note cc-save-note">You can change your appearance later from Character.</p>
        <section id="cc-stats-page" hidden><div class="cc-section-heading"><h3>Character stats</h3><button type="button" id="cc-stats-close">Back to editing</button></div><div id="cc-stats"></div></section>
      </form>`;
    document.body.appendChild(this.root);
    this.mannequin = new Avatar(scene, { crystalWings: true });
    this.mannequin.group.visible = false;
    this.mannequin.standOnFlatGround();
    const key = new THREE.DirectionalLight(0xffecd7, 3.1); key.position.set(4, 6, 5);
    const rim = new THREE.DirectionalLight(0x73b9ff, 1.8); rim.position.set(-4, 3, -5);
    this.lights.add(key, rim, new THREE.HemisphereLight(0xc5d8ff, 0x10131d, 1.5));
    this.el<HTMLSelectElement>("#cc-body").replaceChildren(...BODY_IDS.filter(v => v !== "block").map(v => new Option(v === "female" ? "Female" : "Male", v)));
    for (const [axis, label] of Object.entries(BODY_SHAPE_AXES)) {
      const field = document.createElement("label"); field.className = "cc-field cc-shape-field";
      field.innerHTML = `<span>${label}</span><output id="cc-shape-value-${axis}">0</output><input type="range" data-shape="${axis}" min="-100" max="100" step="1" value="0" />`;
      this.el("#cc-shape-controls").append(field);
    }
    for (const [selector, colors, field, names] of COLORS) {
      colors.forEach((color, i) => {
        const b = document.createElement("button"); b.type = "button"; b.className = "cc-swatch";
        b.dataset.field = field; b.dataset.color = String(color); b.style.setProperty("--swatch", hex(color));
        b.title = names[i]; b.setAttribute("aria-label", names[i]); b.setAttribute("aria-pressed", "false");
        this.el(selector).append(b);
      });
    }
    this.root.addEventListener("input", e => this.onInput(e));
    this.root.addEventListener("click", e => this.onClick(e));
    this.el("form").addEventListener("submit", e => { e.preventDefault(); void this.submit(); });
    this.root.addEventListener("keydown", e => {
      if (e.key !== "Tab") return;
      const buttons = [...this.root.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), summary')].filter(el => el.getClientRects().length && !el.closest('[inert]'));
      const first = buttons[0], last = buttons.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    });
  }
  private el<T extends HTMLElement = HTMLElement>(selector: string): T { return this.root.querySelector<T>(selector)!; }
  private owned(): readonly string[] { return this.options?.ownedTokens?.() ?? STARTER_UNLOCKS; }

  open(options: CharacterCreatorOptions): void {
    if (this.active) this.close();
    this.session++;
    this.options = options;
    this.style = { ...DEFAULT_STYLE, ...options.style, bodyBuild: validateBodyBuild(options.style.bodyBuild), bodyShape: validateBodyShape(options.style.bodyShape) };
    this.active = true; this.busy = false; this.wardrobeBusy = false;
    this.selectedOutfit = undefined; this.outfits = []; this.showClothing = options.showClothing ?? false;
    this.previousFocus = document.activeElement as HTMLElement | null;
    this.previousFov = this.camera.fov; this.previousFilmOffset = this.camera.filmOffset;
    if (this.fadeTimer !== null) { clearTimeout(this.fadeTimer); this.fadeTimer = null; }
    this.root.classList.add("visible"); void this.root.offsetWidth; this.root.classList.add("open");
    this.root.setAttribute("aria-hidden", "false");
    const edit = options.mode === "edit";
    this.el("#cc-title").textContent = options.title ?? (edit ? "Your character" : "Create your character");
    this.el(".cc-panel .cc-kicker").textContent = edit ? "MAKE IT YOURS" : "YOUR JOURNEY STARTS HERE";
    this.el("#cc-submit").textContent = options.submitLabel ?? (edit ? "Save changes" : "Begin journey →");
    this.el("#cc-save-note").textContent = options.saveNote ?? "You can change your appearance later from Character.";
    this.el<HTMLButtonElement>("#cc-submit").disabled = false;
    this.el("#cc-cancel").hidden = !options.onCancel;
    this.el("#cc-identity").hidden = edit;
    this.el<HTMLInputElement>("#cc-name").value = options.name;
    this.el<HTMLInputElement>("#cc-outfit-name").value = "";
    this.el("#cc-wardrobe").hidden = !options.wardrobe;
    this.el("#cc-wardrobe-status").textContent = "";
    this.el("#cc-stats-toggle").hidden = !options.stats;
    this.showStats(false); this.refreshStats();
    this.el<HTMLSelectElement>("#cc-expression").value = "neutral";
    this.mannequin.faceRig.setEmote(null); this.mannequin.faceRig.stopSpeaking();
    this.scene.add(this.lights);
    this.mannequin.group.position.set(0, -1, 0);
    this.mannequin.group.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
    this.camera.up.set(0, 1, 0); this.camera.filmOffset = 0; this.camera.fov = 40;
    this.orbit = new OrbitControls(this.camera, this.canvas);
    this.orbit.enableDamping = true; this.orbit.enablePan = false;
    this.orbit.minPolarAngle = Math.PI * .28; this.orbit.maxPolarAngle = Math.PI * .65;
    this.page = edit ? "face" : "body";
    this.syncControls(); this.setMode(options.initialMode ?? "appearance"); this.refreshPreview(); this.setError("");
    if (options.wardrobe) void this.loadOutfits();
    const session = this.session;
    queueMicrotask(() => { if (this.active && session === this.session) this.el(edit ? '[data-mode="appearance"]' : "#cc-name").focus(); });
  }

  close(): void {
    if (!this.active) return;
    this.setBusy(false);
    this.active = false; this.session++; this.previewRevision++;
    this.root.classList.remove("open");
    this.fadeTimer ??= window.setTimeout(() => { this.root.classList.remove("visible"); this.fadeTimer = null; }, 260);
    this.root.setAttribute("aria-hidden", "true");
    this.mannequin.group.visible = false; this.scene.remove(this.lights);
    this.camera.clearViewOffset(); this.camera.fov = this.previousFov; this.camera.filmOffset = this.previousFilmOffset; this.camera.updateProjectionMatrix();
    this.orbit?.dispose(); this.orbit = null; this.options = null;
    this.previousFocus?.focus({ preventScroll: true });
  }
  refreshStats(): void { if (this.options?.stats) this.el("#cc-stats").innerHTML = this.options.stats.render(); }
  /** Collection pushes can arrive while the editor is open. */
  refreshWardrobe(): void { if (this.active) this.syncOutfitControls(); }
  setError(message: string): void { this.el("#cc-error").textContent = message; }

  update(dt: number): void {
    if (!this.active) return;
    this.orbit?.update();
    const rect = this.el(".cc-preview").getBoundingClientRect();
    const canvas = this.canvas.getBoundingClientRect();
    const w = canvas.width || window.innerWidth, h = canvas.height || window.innerHeight;
    const frameSize = `${w}:${h}:${rect.width}:${rect.height}`;
    if (frameSize !== this.frameSize) { this.frameSize = frameSize; this.frame(this.focus); }
    // Offset the full canvas to the centre of the unobscured preview, on either layout.
    this.camera.setViewOffset(w, h, w / 2 - (rect.left + rect.width / 2 - canvas.left), h / 2 - (rect.top + rect.height / 2 - canvas.top), w, h);
    if (this.el<HTMLSelectElement>("#cc-expression").value === "speaking" && !this.mannequin.faceRig.speaking) {
      this.mannequin.faceRig.speak("Every journey starts with a choice. Let's see what lies beyond the horizon.", 14);
    }
    this.mannequin.update(dt, 0, true, false);
  }

  private setMode(mode: EditorMode): void {
    this.editorMode = mode; this.showStats(false);
    this.root.querySelectorAll<HTMLElement>("[data-mode-page]").forEach(p => { p.hidden = p.dataset.modePage !== mode; });
    this.root.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach(b => { b.classList.toggle("active", b.dataset.mode === mode); b.setAttribute("aria-pressed", String(b.dataset.mode === mode)); });
    this.el("#cc-random").hidden = mode !== "appearance";
    this.el("#cc-clothing").hidden = mode === "outfit";
    if (mode === "appearance") this.showTab(this.page); else this.frame("body");
    this.refreshPreview();
  }
  private showTab(tab: AppearancePage): void {
    this.page = tab;
    this.root.querySelectorAll<HTMLElement>("[data-page]").forEach(p => { p.hidden = p.dataset.page !== tab; });
    this.root.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach(b => { b.classList.toggle("active", b.dataset.tab === tab); b.setAttribute("aria-pressed", String(b.dataset.tab === tab)); });
    this.frame(tab === "body" ? "body" : "face");
    this.el(".cc-scroll").scrollTop = 0;
  }
  private frame(focus: "face" | "body"): void {
    this.focus = focus;
    this.root.querySelectorAll<HTMLElement>("[data-focus]").forEach(b => { b.classList.toggle("active", b.dataset.focus === focus); b.setAttribute("aria-pressed", String(b.dataset.focus === focus)); });
    if (!this.orbit) return;
    const face = focus === "face";
    // Use the same relaxed neck alignment as the live character, with a still,
    // level face for selecting features. Exact bind remains an authoring pose.
    this.mannequin.setDebugPose(face ? "portrait" : null);
    const stature = validateBodyShape(this.style.bodyShape).stature;
    const hair = face && this.page === "hair" && this.editorMode === "appearance";
    const y = face ? (hair ? .64 : .60) + stature * .085 : -.1;
    // Short screens need a wider vertical field to reserve space for the toolbar.
    const h = this.canvas.clientHeight || window.innerHeight;
    const previewH = this.el(".cc-preview").getBoundingClientRect().height;
    const compact = this.el(".cc-preview").getBoundingClientRect().width === this.root.clientWidth;
    const scale = Math.max(1, h / Math.max(140, previewH - (compact ? 110 : face ? 175 : 200)));
    this.orbit.minDistance = (face ? .38 : 1.8) * scale; this.orbit.maxDistance = (face ? 2 : 6) * scale;
    this.orbit.target.set(0, y, 0);
    this.camera.position.set(0, y + (face ? .015 : .15), (face ? hair ? 1.05 : .76 : 2.9) * scale);
    this.orbit.update();
  }
  private refreshPreview(): void {
    const revision = ++this.previewRevision;
    const style = { ...this.style };
    if (this.editorMode === "appearance" && !this.showClothing) for (const part of STYLE_PARTS) style[part] = "none";
    this.el("#cc-clothing").textContent = this.showClothing ? "Hide outfit" : "Show outfit";
    this.el("#cc-clothing").setAttribute("aria-pressed", String(this.showClothing));
    this.mannequin.setStyle(style);
    this.el("#cc-loading").hidden = false;
    void this.mannequin.styleSettled().then(() => {
      if (!this.active || revision !== this.previewRevision) return;
      this.mannequin.group.visible = true; this.el("#cc-loading").hidden = true;
    });
    const name = this.el<HTMLInputElement>("#cc-name").value.trim() || "Your character";
    this.el("#cc-preview-name").textContent = name;
    const head = HEAD_CATALOG[this.style.body === "female" ? "female" : "male"].find(h => h.id === validateFaceId(this.style.face, this.style.body));
    this.el("#cc-preview-caption").textContent = this.editorMode === "outfit" ? "Wear the stories you’ve earned." : `${this.style.body === "female" ? "Female" : "Male"} · ${BODY_BUILDS.find(b => b.id === validateBodyBuild(this.style.bodyBuild))!.label} · ${head?.label ?? "Heroic / Balanced"}`;
  }

  private onClick(event: MouseEvent): void {
    const b = (event.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!b || b.disabled || this.busy) return;
    if (b.dataset.mode) this.setMode(b.dataset.mode as EditorMode);
    else if (b.dataset.tab) this.showTab(b.dataset.tab as AppearancePage);
    else if (b.dataset.focus) this.frame(b.dataset.focus as "face" | "body");
    else if (b.dataset.rotate && this.orbit) {
      const offset = this.camera.position.clone().sub(this.orbit.target).applyAxisAngle(new THREE.Vector3(0, 1, 0), Number(b.dataset.rotate) * Math.PI / 6);
      this.camera.position.copy(this.orbit.target).add(offset); this.orbit.update();
    } else if (b.dataset.bodyBuild) {
      this.style.bodyBuild = validateBodyBuild(b.dataset.bodyBuild);
      this.style.bodyShape = validateBodyShape(undefined);
      this.syncControls(); this.refreshPreview(); this.frame(this.focus);
    } else if (b.dataset.face) {
      this.style.face = validateFaceId(b.dataset.face, this.style.body); this.syncControls(); this.refreshPreview();
    } else if (b.dataset.hair) {
      this.style.hair = validateHairId(b.dataset.hair, this.style.body); this.syncControls(); this.refreshPreview();
    } else if (b.dataset.field) {
      this.style[b.dataset.field as ColorField] = Number(b.dataset.color); this.syncSwatches(); this.refreshPreview();
    } else if (b.dataset.outfit) {
      const doc = this.outfits.find(o => o.id === b.dataset.outfit);
      if (doc) { this.style = wearOutfit(this.style, doc.style, this.owned()); this.selectedOutfit = doc; this.el<HTMLInputElement>("#cc-outfit-name").value = doc.name; this.syncControls(); this.renderOutfits(); this.refreshPreview(); }
    } else if (b.dataset.deleteOutfit) void this.deleteOutfit(b.dataset.deleteOutfit);
    else if (b.dataset.track) this.options?.stats?.onAction(b.dataset.track);
    else switch (b.id) {
      case "cc-clothing": this.showClothing = !this.showClothing; this.refreshPreview(); break;
      case "cc-reset-view": this.frame(this.focus); break;
      case "cc-stats-toggle": this.showStats(this.el("#cc-stats-page").hidden); break;
      case "cc-stats-close": this.showStats(false); break;
      case "cc-random": this.randomize(); break;
      case "cc-revert": this.reset(); break;
      case "cc-cancel": this.options?.onCancel?.(); break;
      case "cc-outfit-save": void this.saveOutfit(false); break;
      case "cc-outfit-update": void this.saveOutfit(true); break;
    }
  }
  private onInput(event: Event): void {
    if (this.busy) return;
    const el = event.target as HTMLInputElement | HTMLSelectElement;
    if (el.id === "cc-expression") { this.mannequin.faceRig.stopSpeaking(); this.mannequin.faceRig.setEmote(el.value); return; }
    if (el.id === "cc-outfit-name") return;
    if (el.id === "cc-body") { this.style.body = el.value as CharacterStyle["body"]; this.style.face = "base"; this.style.hair = validateHairId(this.style.hair, this.style.body); }
    else if (el.id === "cc-archetype") {
      const p = BODY_ARCHETYPES.find(p => p.id === el.value);
      if (p) { this.style.bodyShape = validateBodyShape(p.shape); if (p.id === "elder") this.style.hairColor = 0xd8d5cf; }
    } else if (el.dataset.shape) this.style.bodyShape = validateBodyShape({ ...this.style.bodyShape, [el.dataset.shape]: Number(el.value) / 100 });
    else if (el.id === "cc-hair") this.style.hair = el.value as CharacterStyle["hair"];
    else if (el.dataset.part) {
      const part = el.dataset.part as StylePart;
      if (el.value !== "none" && el.value !== DEFAULT_STYLE[part] && !this.owned().includes(`${part}:${el.value}`)) return;
      this.style[part] = el.value;
      this.style.hiddenGear = (this.style.hiddenGear ?? []).filter(p => p !== part);
      if (el.value === "none") this.style.hiddenGear.push(part);
      this.selectedOutfit = null; this.renderOutfits();
    } else if (el.id !== "cc-name") return;
    this.syncControls(); this.refreshPreview(); this.setError("");
    if (el.id === "cc-body" || el.id === "cc-archetype" || el.dataset.shape === "stature") this.frame(this.focus);
  }
  private syncControls(): void {
    this.el<HTMLSelectElement>("#cc-body").value = this.style.body;
    this.root.querySelectorAll<HTMLButtonElement>("[data-body-build]").forEach(b => {
      const selected = b.dataset.bodyBuild === validateBodyBuild(this.style.bodyBuild);
      b.classList.toggle("selected", selected); b.setAttribute("aria-pressed", String(selected));
    });
    this.el("#cc-shirtc").closest("fieldset")!.hidden = this.style.torso !== "rookie";
    this.el("#cc-pantsc").closest("fieldset")!.hidden = this.style.legs !== "rookie";
    const hair = this.el<HTMLSelectElement>("#cc-hair");
    hair.replaceChildren(...HAIR_CATALOG[this.style.body === "female" ? "female" : "male"].map(h => new Option(h.label, h.id)));
    hair.value = validateHairId(this.style.hair, this.style.body);
    this.renderHair();
    const presets = this.el<HTMLSelectElement>("#cc-archetype");
    presets.replaceChildren(new Option("Custom", "custom"), ...BODY_ARCHETYPES.map(p => new Option(p[this.style.body === "female" ? "female" : "male"], p.id)));
    presets.value = BODY_ARCHETYPES.find(p => bodyShapeKey(p.shape) === bodyShapeKey(this.style.bodyShape))?.id ?? "custom";
    const shape = validateBodyShape(this.style.bodyShape);
    this.root.querySelectorAll<HTMLInputElement>("[data-shape]").forEach(input => {
      const value = Math.round(shape[input.dataset.shape as BodyShapeAxis] * 100); input.value = String(value);
      this.el(`#cc-shape-value-${input.dataset.shape}`).textContent = `${value > 0 ? "+" : ""}${value}`;
    });
    this.renderFaces(); this.syncSwatches(); this.syncOutfitControls();
  }
  private renderFaces(): void {
    const body = this.style.body === "female" ? "female" : "male";
    const choices = PLAYER_HEAD_CATALOG[body];
    const container = this.el("#cc-faces");
    // Reuse cards while changing colours or sliders so keyboard focus is stable.
    if (container.dataset.body !== body) {
      container.dataset.body = body; container.replaceChildren();
      for (const face of choices) {
        const b = document.createElement("button"); b.type = "button"; b.className = "cc-face-card"; b.dataset.face = face.id;
        b.innerHTML = `<img src="/images/character/${body}-${face.id}.webp" alt="" /><span class="cc-face-label">${face.label}<span class="cc-choice-mark" aria-hidden="true">✓</span></span><small>${face.description}</small>`;
        container.append(b);
      }
    }
    container.querySelectorAll<HTMLButtonElement>("button").forEach(b => { const selected = b.dataset.face === validateFaceId(this.style.face, body); b.classList.toggle("selected", selected); b.setAttribute("aria-pressed", String(selected)); });
    this.el("#cc-face-count").textContent = `${choices.length} faces`;
    this.el("#cc-face-note").textContent = "Every face has its own expressions.";
  }
  private renderHair(): void {
    const container = this.el("#cc-hairstyles");
    const body = this.style.body === "female" ? "female" : "male";
    container.hidden = false;
    if (container.dataset.body !== body) {
      container.dataset.body = body; container.replaceChildren();
      for (const hair of HAIR_CATALOG[body].filter(h => h.id !== "none")) {
        const b = document.createElement("button"); b.type = "button"; b.className = "cc-hair-card"; b.dataset.hair = hair.id;
        const cut = "cut" in hair ? `<small>${hair.cut}</small>` : "";
        b.innerHTML = `<img src="/images/hair/${body}-${hair.id}.webp" alt="" loading="lazy"><span>${hair.label}${cut}</span>`;
        container.append(b);
      }
    }
    container.querySelectorAll<HTMLButtonElement>("button").forEach(b => {
      const selected = b.dataset.hair === this.style.hair;
      b.classList.toggle("selected", selected); b.setAttribute("aria-pressed", String(selected));
    });
  }
  private syncSwatches(): void {
    this.root.querySelectorAll<HTMLButtonElement>(".cc-swatch").forEach(b => { const selected = Number(b.dataset.color) === this.style[b.dataset.field as ColorField]; b.classList.toggle("selected", selected); b.setAttribute("aria-pressed", String(selected)); });
  }
  private syncOutfitControls(): void {
    const owned = this.owned();
    this.root.querySelectorAll<HTMLSelectElement>("select[data-part]").forEach(select => {
      const part = select.dataset.part as StylePart;
      select.replaceChildren(...STYLE_VARIANTS[part].map(v => { const locked = v !== "none" && v !== DEFAULT_STYLE[part] && !owned.includes(`${part}:${v}`); const option = new Option(v === "none" ? "None" : v === "rookie" ? ({ head: "Rookie", torso: "Rookie · Shirt", arms: "Rookie · Right bracelet", legs: "Rookie · Belted trousers", feet: "Rookie · Boots" }[part]) : `${v[0].toUpperCase() + v.slice(1)}${locked ? " · Locked" : ""}`, v); option.disabled = locked; return option; }));
      select.value = this.style[part];
    });
    this.el("#cc-outfit-note").textContent = this.options?.mode === "create" ? "Begin with your base layers. Discover clothing and armor as you explore." : "Choose from your unlocked looks. Changing a look keeps your equipment stats.";
  }
  private showStats(show: boolean): void {
    this.el("#cc-stats-page").hidden = !show; this.el("#cc-stats-toggle").setAttribute("aria-expanded", String(show));
    for (const child of this.el(".cc-panel").children) if (child.id !== "cc-stats-page") (child as HTMLElement).inert = show;
    if (show) { this.refreshStats(); this.el("#cc-stats-close").focus(); }
  }
  private reset(): void {
    if (!this.options) return;
    this.style = { ...DEFAULT_STYLE, ...this.options.style, bodyBuild: validateBodyBuild(this.options.style.bodyBuild), bodyShape: validateBodyShape(this.options.style.bodyShape) };
    this.selectedOutfit = undefined; this.syncControls(); this.renderOutfits(); this.refreshPreview(); this.frame(this.focus); this.setError("");
  }
  private randomize(): void {
    const pick = <T>(v: readonly T[]): T => v[Math.floor(Math.random() * v.length)];
    this.style = { ...this.style, bodyBuild: pick(BODY_BUILDS).id, bodyShape: validateBodyShape(undefined), face: pick(PLAYER_HEAD_CATALOG[this.style.body === "female" ? "female" : "male"].map(f => f.id)),
      hair: pick<NonNullable<CharacterStyle["hair"]>>(HAIR_CATALOG[this.style.body === "female" ? "female" : "male"].map(h => h.id)), skin: pick(SKIN_COLORS), hairColor: pick(HAIR_COLORS), eyeColor: pick(EYE_COLORS), auraColor: pick(AURA_COLORS) };
    this.syncControls(); this.refreshPreview(); this.frame(this.focus);
  }

  private async loadOutfits(): Promise<void> {
    const session = this.session;
    this.el("#cc-wardrobe-status").textContent = "Loading outfits…";
    try { const docs = await this.options!.wardrobe!.list(); if (!this.active || session !== this.session) return; this.outfits = [...this.outfits, ...docs.filter(d => !this.outfits.some(existing => existing.id === d.id))]; this.renderOutfits(); this.el("#cc-wardrobe-status").textContent = ""; }
    catch { if (session === this.session) this.el("#cc-wardrobe-status").textContent = "Couldn’t load outfits. Reopen Character to try again."; }
  }
  private renderOutfits(): void {
    const container = this.el("#cc-outfits"); container.replaceChildren();
    if (!this.outfits.length) { const p = document.createElement("p"); p.className = "cc-empty"; p.textContent = "Your saved looks will appear here."; container.append(p); }
    for (const outfit of this.outfits) {
      const row = document.createElement("div"); row.className = "cc-outfit-card";
      const b = document.createElement("button"); b.type = "button"; b.dataset.outfit = outfit.id; b.textContent = outfit.name;
      b.setAttribute("aria-pressed", String(this.selectedOutfit?.id === outfit.id));
      const remove = document.createElement("button"); remove.type = "button"; remove.dataset.deleteOutfit = outfit.id; remove.textContent = "×"; remove.setAttribute("aria-label", `Delete outfit ${outfit.name}`);
      b.disabled = remove.disabled = this.wardrobeBusy;
      row.append(b, remove); container.append(row);
    }
    this.el<HTMLButtonElement>("#cc-outfit-update").disabled = this.wardrobeBusy || !this.selectedOutfit;
    this.el<HTMLButtonElement>("#cc-outfit-save").disabled = this.wardrobeBusy;
  }
  private async saveOutfit(update: boolean): Promise<void> {
    const wardrobe = this.options?.wardrobe;
    if (!wardrobe || this.wardrobeBusy) return;
    const name = this.el<HTMLInputElement>("#cc-outfit-name").value.trim();
    if (!name) { this.el("#cc-wardrobe-status").textContent = "Give this outfit a name first."; this.el("#cc-outfit-name").focus(); return; }
    const session = this.session;
    this.wardrobeBusy = true; this.renderOutfits(); this.el("#cc-wardrobe-status").textContent = "Saving outfit…";
    try {
      const doc = await wardrobe.save(update ? this.selectedOutfit?.id ?? null : null, name, clampStyleToUnlocks({ ...this.style }, new Set(this.owned())));
      if (!this.active || session !== this.session) return;
      this.outfits = [doc, ...this.outfits.filter(d => d.id !== doc.id)]; this.selectedOutfit = doc;
      this.el("#cc-wardrobe-status").textContent = `Saved “${doc.name}”.`;
    } catch { if (session === this.session) this.el("#cc-wardrobe-status").textContent = "Couldn’t save outfit. Please try again."; }
    finally { if (session === this.session) { this.wardrobeBusy = false; this.renderOutfits(); } }
  }
  private async deleteOutfit(id: string): Promise<void> {
    if (!this.options?.wardrobe || this.wardrobeBusy) return;
    const session = this.session; this.wardrobeBusy = true; this.renderOutfits();
    try {
      await this.options.wardrobe.remove(id);
      if (!this.active || session !== this.session) return;
      this.outfits = this.outfits.filter(d => d.id !== id); if (this.selectedOutfit?.id === id) this.selectedOutfit = null;
      this.el("#cc-wardrobe-status").textContent = "Outfit deleted.";
    } catch { if (session === this.session) this.el("#cc-wardrobe-status").textContent = "Couldn’t delete outfit. Please try again."; }
    finally { if (session === this.session) { this.wardrobeBusy = false; this.renderOutfits(); } }
  }
  private async submit(): Promise<void> {
    const options = this.options;
    if (!options || this.busy || this.wardrobeBusy) return;
    let name = options.name;
    if (options.mode === "create") {
      const result = normalizeCharacterName(this.el<HTMLInputElement>("#cc-name").value);
      if (!result.ok) { this.setError(result.problem === "required" ? "Choose a name for your character." : result.problem === "length" ? `Name must be ${CHARACTER_NAME_MIN}–${CHARACTER_NAME_MAX} characters.` : "Use letters, numbers, spaces, apostrophes, or hyphens."); this.el("#cc-name").focus(); return; }
      name = result.display;
    }
    const session = this.session;
    this.setBusy(true);
    this.setError(options.mode === "create" ? "Preparing your journey…" : "");
    try { await options.onSubmit({ name, style: clampStyleToUnlocks({ ...this.style, bodyBuild: validateBodyBuild(this.style.bodyBuild), bodyShape: validateBodyShape(this.style.bodyShape) }, new Set(this.owned())) }, this.selectedOutfit); }
    catch { if (session === this.session) this.setError("Couldn’t save your character. Please try again."); }
    finally { if (session === this.session) this.setBusy(false); }
  }
  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.root.setAttribute("aria-busy", String(busy));
    if (busy) {
      this.root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>("input, select, button").forEach(el => {
        this.disabledForSave.set(el, el.disabled); el.disabled = true;
      });
    } else {
      for (const [el, disabled] of this.disabledForSave) el.disabled = disabled;
      this.disabledForSave.clear();
    }
  }
}
